import type { StatusGmud } from '../../../types/gmud'

/**
 * As sete etapas do processo de gestão de mudanças, e quem responde por cada uma.
 *
 * **Transcritas do ANEXO XXI (p. 193), inclusive o responsável.** Não é rótulo de tela: é a
 * atribuição contratual, e é ela que responde "de quem estamos esperando agora?" — a pergunta que a
 * etiqueta de status sozinha nunca respondeu.
 *
 * **A etapa não é uma tabela.** É projeção do status, como o plano decidiu: um segundo lugar
 * guardando "em que etapa está" divergiria do status no primeiro dia em que alguém corrigisse um
 * pelo psql. Aqui a etapa é derivada, e derivada não diverge.
 */
export interface EtapaDoProcesso {
  numero: number
  titulo: string
  /** Quem o TR atribui. `AMBOS` é literal do anexo: a etapa 6 é "CONTRATADA E CONTRATANTE". */
  responsavel: 'CONTRATADA' | 'CONTRATANTE' | 'AMBOS'
  /** O que o contrato pede naquela etapa, em uma frase. */
  resumo: string
}

export const ETAPAS: EtapaDoProcesso[] = [
  {
    numero: 1,
    titulo: 'Planejamento da mudança',
    responsavel: 'CONTRATADA',
    resumo: 'O plano detalhado, com cronograma, recursos, procedimentos de implementação e plano de contingência.',
  },
  {
    numero: 2,
    titulo: 'Solicitação formal',
    responsavel: 'CONTRATADA',
    resumo: 'O formulário com a natureza da mudança, o impacto esperado e os motivos, encaminhado ao comitê.',
  },
  {
    numero: 3,
    titulo: 'Avaliação pelo comitê',
    responsavel: 'CONTRATANTE',
    resumo: 'O comitê analisa impacto, riscos, disponibilidade de recursos e conformidade — e pode pedir mais informações.',
  },
  {
    numero: 4,
    titulo: 'Aprovação do plano',
    responsavel: 'CONTRATANTE',
    resumo: 'O plano é submetido ao comitê para aprovação final, que pode pedir ajustes antes de aprovar.',
  },
  {
    numero: 5,
    titulo: 'Execução da mudança',
    responsavel: 'CONTRATADA',
    resumo: 'A implementação, conforme o plano aprovado, com acompanhamento rigoroso.',
  },
  {
    numero: 6,
    titulo: 'Verificação e validação',
    responsavel: 'AMBOS',
    resumo: 'Os testes anexados como evidência obrigatória. Falha detectada bloqueia a validação até a correção.',
  },
  {
    numero: 7,
    titulo: 'Fechamento',
    responsavel: 'CONTRATADA',
    resumo: 'O encerramento formal, com resultados documentados, evidências e lições aprendidas.',
  },
]

/**
 * Como cada responsável do contrato se escreve na tela.
 *
 * Mora junto das etapas, e não na tela que por acaso o exibiu primeiro, porque agora são duas: a
 * projeção das sete etapas no detalhe e o cabeçalho de cada coluna do quadro. Mapa copiado é texto
 * que diverge na primeira correção de redação — e aqui o que divergiria é a atribuição contratual
 * de quem responde pela etapa (Regra nº 4).
 *
 * "Prefeitura" e não "Contratante": o público são fiscais e servidores leigos em TI, e a palavra do
 * contrato não é a palavra de quem lê a tela.
 */
export const RESPONSAVEL_NA_TELA: Record<EtapaDoProcesso['responsavel'], string> = {
  CONTRATADA: 'Contratada',
  CONTRATANTE: 'Prefeitura',
  AMBOS: 'Contratada e Prefeitura',
}

/**
 * Em que etapa do anexo cada estado do sistema cai.
 *
 * `Record` completo sem `default`: um estado novo quebra o build aqui, em vez de cair em silêncio
 * numa etapa que não é a dele. `null` é resposta legítima — os quatro estados terminais fora do
 * fluxo e o histórico importado não estão em etapa nenhuma.
 */
const ETAPA_DO_STATUS: Record<StatusGmud, number | null> = {
  RASCUNHO: 1,
  SOLICITADA: 2,
  EM_AVALIACAO_CAB: 3,
  AGUARDANDO_AJUSTES: 4,
  APROVADA: 4,
  EM_EXECUCAO: 5,
  EM_VERIFICACAO: 6,
  CONCLUIDA: 7,
  // Fora do fluxo. Recusada e cancelada param antes do fim; revertida voltou da execução; e o
  // histórico importado nunca percorreu este processo — dizer que ele está na etapa 7 afirmaria
  // que as 1.027 solicitações passaram por comitê, que é justamente o que elas não fizeram.
  RECUSADA: null,
  CANCELADA: null,
  REVERTIDA_ROLLBACK: null,
  MIGRADO_V1: null,
}

/**
 * A etapa em que a solicitação está.
 *
 * @param status o estado atual
 * @returns o número da etapa, ou nulo quando o estado está fora do fluxo do anexo
 */
export function etapaAtual(status: StatusGmud): number | null {
  return ETAPA_DO_STATUS[status]
}

/**
 * De quem é a PRÓXIMA ação em cada estado — que não é o responsável contratual da etapa.
 *
 * A etapa 2 ("Solicitação formal") é da contratada no ANEXO XXI, mas uma solicitação em
 * "Aguardando pauta" espera o PRESIDENTE pautá-la; a etapa 4 é da Prefeitura, mas "Aguardando
 * ajustes" espera a contratada reenviar e "Aprovada" espera o executor começar. O quadro existe
 * para responder "de quem estamos esperando" e respondia errado justamente onde comitê e
 * contratada se cobram. A fonte é a matriz de transições (V3): a saída "para frente" de cada
 * estado e o lado que ela exige. `etapasDoAnexoXxi.test.ts` cobra cada entrada contra a matriz.
 */
export const PROXIMA_ACAO_DE: Record<StatusGmud, 'CONTRATADA' | 'CONTRATANTE' | null> = {
  RASCUNHO: 'CONTRATADA',
  SOLICITADA: 'CONTRATANTE',
  EM_AVALIACAO_CAB: 'CONTRATANTE',
  AGUARDANDO_AJUSTES: 'CONTRATADA',
  APROVADA: 'CONTRATADA',
  EM_EXECUCAO: 'CONTRATADA',
  EM_VERIFICACAO: 'CONTRATANTE',
  CONCLUIDA: null,
  RECUSADA: null,
  CANCELADA: null,
  REVERTIDA_ROLLBACK: null,
  MIGRADO_V1: null,
}

/** A próxima ação de uma coluna do quadro: os estados de cada etapa esperam o mesmo lado. */
export function proximaAcaoDaEtapa(numero: number): 'CONTRATADA' | 'CONTRATANTE' | null {
  const status = (Object.keys(ETAPA_DO_STATUS) as StatusGmud[]).find((s) => ETAPA_DO_STATUS[s] === numero)
  return status ? PROXIMA_ACAO_DE[status] : null
}

/**
 * A etapa 7 diverge do contrato, e a tela tem de dizer isso.
 *
 * O ANEXO XXI atribui o fechamento à **CONTRATADA**; neste sistema quem encerra é a CONTRATANTE.
 * A divergência está registrada como a decisão nº 4 do dono e ainda não foi resolvida — escondê-la
 * daria à contratada um argumento pronto numa contestação, encontrado por ela e não por nós.
 */
export const ETAPA_COM_DIVERGENCIA = 7
